import { ArrowBack, CorporateFareRounded } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "react-query";
import MainLayout from "../../layouts/main/main_layout";
import Loading from "../../components/loading/loading";
import RequiredMark from "../../components/required_mark/required_mark";
import { useFeedback } from "../../providers/feedback/feeedback";
import { queryClient } from "../../providers/query/query";
import {
  createOrganization,
  getModulesCatalog,
  getOrganization,
  updateOrganization,
} from "../../services/organizations/organizations";
import { organizations } from "../../routes/routes_path";
import {
  OrganizationDetails,
  OrganizationModule,
  OrganizationStatus,
} from "./model/organization";
import { black, greyEnabled, purple, purpleLight } from "../../theme/colors";

const STATUS_OPTIONS: { value: OrganizationStatus; label: string }[] = [
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
  { value: "archived", label: "Archived" },
];

interface OrganizationFormInputs {
  orgCode: string;
  name: string;
  status: OrganizationStatus;
  tier: string;
  moduleIds: number[];
}

const EMPTY_FORM: OrganizationFormInputs = {
  orgCode: "",
  name: "",
  status: "active",
  tier: "",
  moduleIds: [],
};

export default function AddEditOrganization() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { showSnackbar } = useFeedback();
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<OrganizationFormInputs>({ defaultValues: EMPTY_FORM });

  const { data: modulesCatalog, isLoading: isModulesLoading } = useQuery(
    ["organization-modules-catalog"],
    async () => {
      let response = await getModulesCatalog();
      return (response?.data?.data?.list || []) as OrganizationModule[];
    }
  );

  const { data: organizationDetails, isLoading: isDetailsLoading } = useQuery(
    ["organization-details", id],
    async () => {
      let response = await getOrganization(id!);
      return response.data.data as OrganizationDetails;
    },
    { enabled: !!id }
  );

  useEffect(() => {
    if (organizationDetails) {
      reset({
        orgCode: organizationDetails.orgCode,
        name: organizationDetails.name,
        status: organizationDetails.status,
        tier: organizationDetails.tier || "",
        moduleIds: organizationDetails.moduleSubscriptions
          .filter((sub) => sub.status === "active")
          .map((sub) => sub.moduleId),
      });
    }
  }, [organizationDetails, reset]);

  const onSubmit: SubmitHandler<OrganizationFormInputs> = (data) => {
    setSubmitting(true);
    const request = isEdit
      ? updateOrganization(id!, {
          name: data.name,
          status: data.status,
          tier: data.tier || null,
          moduleIds: data.moduleIds,
        })
      : createOrganization({
          orgCode: data.orgCode,
          name: data.name,
          status: data.status,
          tier: data.tier || null,
          moduleIds: data.moduleIds,
        });
    request
      .then(() => {
        showSnackbar(isEdit ? "Organisation updated successfully" : "Organisation added successfully");
        queryClient.invalidateQueries({ queryKey: ["organizations"] });
        navigate(organizations);
      })
      .catch((e) => {
        showSnackbar(
          e?.response?.data?.error?.message ||
            `Something went wrong while ${isEdit ? "updating" : "adding"} the organisation.`
        );
      })
      .finally(() => setSubmitting(false));
  };

  const isPageLoading = isModulesLoading || (isEdit && isDetailsLoading);

  return (
    <MainLayout>
      <Box>
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBack />
          </IconButton>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: purpleLight,
                flexShrink: 0,
              }}
            >
              <CorporateFareRounded sx={{ color: purple, fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={700} color={black}>
                {isEdit ? "Edit Organisation" : "Add Organisation"}
              </Typography>
              <Typography variant="body2" color={greyEnabled}>
                Fill in the organisation's details below.
              </Typography>
            </Box>
          </Box>
        </Box>

        <Card
          elevation={0}
          sx={{ width: "100%", mt: 2, borderRadius: 3, border: "1px solid #ECECEC" }}
        >
          <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
            {isPageLoading ? (
              <Box display="flex" justifyContent="center" py={8}>
                <Loading />
              </Box>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={3}>
                  <Grid item md={6} sm={12}>
                    <TextField
                      fullWidth
                      label={<>Org Code<RequiredMark /></>}
                      placeholder="Enter a unique org code"
                      disabled={isEdit}
                      error={!!errors.orgCode}
                      helperText={
                        errors.orgCode?.message ||
                        (isEdit ? "Org code can't be changed after creation" : undefined)
                      }
                      {...register("orgCode", { required: "Please enter an org code" })}
                    />
                  </Grid>
                  <Grid item md={6} sm={12}>
                    <TextField
                      fullWidth
                      label={<>Name<RequiredMark /></>}
                      placeholder="Enter organisation name"
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      {...register("name", { required: "Please enter the organisation name" })}
                    />
                  </Grid>

                  <Grid item md={6} sm={12}>
                    <Controller
                      name="status"
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} select fullWidth label="Status">
                          {STATUS_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  </Grid>
                  <Grid item md={6} sm={12}>
                    <TextField
                      fullWidth
                      label="Tier (Optional)"
                      placeholder="e.g. standard, premium"
                      {...register("tier")}
                    />
                  </Grid>

                  <Grid item md={12}>
                    <Typography variant="body1" fontWeight={600} color={black} fontSize="1.1rem" mb={1}>
                      Subscribed Modules
                    </Typography>
                    <Controller
                      name="moduleIds"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <Box display="flex" flexWrap="wrap" gap={1}>
                          {(modulesCatalog || []).map((module) => {
                            const checked = value.includes(module.id);
                            return (
                              <FormControlLabel
                                key={module.id}
                                control={
                                  <Checkbox
                                    checked={checked}
                                    onChange={() =>
                                      onChange(
                                        checked
                                          ? value.filter((id) => id !== module.id)
                                          : [...value, module.id]
                                      )
                                    }
                                    sx={{
                                      color: "#ABA0AB",
                                      "&.Mui-checked": { color: purple },
                                    }}
                                  />
                                }
                                label={module.name}
                              />
                            );
                          })}
                        </Box>
                      )}
                    />
                  </Grid>
                </Grid>

                <Box mt={2} mb={2}>
                  <Divider />
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Button
                    variant="outlined"
                    disabled={submitting}
                    onClick={() => navigate(-1)}
                    sx={{
                      minWidth: 150,
                      borderColor: purple,
                      color: purple,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      "&:hover": { borderColor: purple, bgcolor: purpleLight },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disableElevation
                    disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : undefined}
                    sx={{
                      minWidth: 150,
                      bgcolor: purple,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      "&:hover": { bgcolor: "#7A2074" },
                    }}
                  >
                    {isEdit ? "Update Organisation" : "Add Organisation"}
                  </Button>
                </Box>
              </form>
            )}
          </CardContent>
        </Card>
      </Box>
    </MainLayout>
  );
}
